import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { useAdsStyles } from '../../util/styles/hooks/useAdsStyles.js';
import { selectToken } from '../../util/tokenSelector.js';
import { useCombinedRefs } from '../../util/useCombinedRefs.js';
import { isTouchDevice } from '@drone-js/media-query';
import { Box } from '../Box/Box.js';
import { Icon } from '../Icon/Icon.js';
import { Typography } from '../Typography/Typography.js';
import { useExperimentContext } from '../../contexts/session/Experiment.js';
import { useDirectionContext, Direction } from '../../contexts/session/Locale.js';
import { useThemeContext } from '../../contexts/session/Theme.js';
import { ToggleInput, ToggleKnobContainer } from './Toggle.styled.js';

var _excluded = ["id", "label", "labelAlignment", "labelInteractive", "ariaLabel", "textVariant", "width", "selection", "state", "onChange"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * Toggles allow people to complete tasks that involve decision-making. They are used when each item in a set of choices can be controlled independently from the others.
 */
var Toggle = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var id = _ref.id,
    label = _ref.label,
    labelAlignment = _ref.labelAlignment,
    labelInteractive = _ref.labelInteractive,
    ariaLabel = _ref.ariaLabel,
    textVariant = _ref.textVariant,
    width = _ref.width,
    selection = _ref.selection,
    state = _ref.state,
    onChange = _ref.onChange,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _useExperimentContext = useExperimentContext(),
    isToggleButtonAccessibilityColorFix = _useExperimentContext.isToggleButtonAccessibilityColorFix;
  var inputRef = React.useRef(null);
  var combinedRef = useCombinedRefs(ref, inputRef);
  var theme = useThemeContext();
  var direction = useDirectionContext();
  var hasTouch = isTouchDevice();
  var _useAdsStyles = useAdsStyles(),
    getInteractiveState = _useAdsStyles.getInteractiveState;
  var _getInteractiveState = getInteractiveState({
      type: 'filled'
    }),
    boxProps = _getInteractiveState.boxProps;
  var checked = selection === 'selected';
  var expanded = width === 'parent-fit';
  var disabled = state === 'disabled';
  var rippleSize = hasTouch ? '48px' : '32px';
  var rippleColor = selectToken(theme.text, checked ? 'product.primary' : 'base.subtle');
  var selectedKnobSize = hasTouch ? '24px' : '16px';
  var selectedKnobPadding = hasTouch ? '4' : '2';
  var unselectedKnobSize = hasTouch ? '16px' : '12px';
  var unselectedKnobPadding = hasTouch ? '8' : '4';
  var knobSize = checked ? selectedKnobSize : unselectedKnobSize;
  var knobPadding = selectToken(theme.spacing, checked ? selectedKnobPadding : unselectedKnobPadding);
  var knobTranslation = '0px';
  if (checked) knobTranslation = hasTouch ? '20px' : '12px';
  var selectedTrackColor = disabled ? 'deprecated.neutral.disabled-2' : 'product.primary';
  var unselectedTrackColor = disabled ? 'deprecated.neutral.disabled-1' : 'deprecated.neutral.5';
  var trackColor = checked ? selectedTrackColor : unselectedTrackColor;
  var trackWidth = hasTouch ? '52px' : '32px';
  var trackHeight = hasTouch ? '32px' : '20px';
  var textColor = disabled ? 'base.disabled' : 'base.default';
  var outlineColor = selectToken(theme.border, 'product.primary.strong');
  var handleOnChange = React.useCallback(function (event) {
    onChange === null || onChange === void 0 || onChange(event.target.checked);
  }, [onChange]);
  var handleKeyDown = function handleKeyDown(event) {
    if (disabled) return;
    if (event.key === ' ' || event.key === 'Enter') {
      var _inputRef$current;
      event.preventDefault();
      (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.click();
    }
  };
  var input = React.useMemo(function () {
    return /*#__PURE__*/React.createElement(ToggleInput, _extends({}, isToggleButtonAccessibilityColorFix && {
      tabIndex: -1
    }, {
      type: "checkbox",
      role: "switch",
      checked: checked,
      disabled: disabled,
      onChange: handleOnChange,
      "aria-label": ariaLabel,
      id: id,
      ref: isToggleButtonAccessibilityColorFix ? combinedRef : ref,
      $outlineColor: outlineColor
    }));
  }, [isToggleButtonAccessibilityColorFix, checked, disabled, handleOnChange, ariaLabel, id, combinedRef, ref, outlineColor]);
  return /*#__PURE__*/React.createElement(Box, _extends({
    as: labelInteractive ? 'label' : undefined,
    display: "inline-flex",
    alignItems: "center",
    userSelect: "none",
    tapHighlight: false,
    cursor: disabled || !labelInteractive ? undefined : 'pointer',
    flexDirection: labelAlignment === 'start' ? 'row' : 'row-reverse',
    width: label && expanded ? '100%' : undefined,
    p: labelInteractive && hasTouch ? '8' : undefined
  }, labelInteractive && rest), label && /*#__PURE__*/React.createElement(Box, {
    display: "inline-flex",
    flexGrow: expanded ? 1 : undefined
  }, /*#__PURE__*/React.createElement(Typography, {
    tag: "span",
    lineClamp: 1,
    textColor: textColor,
    variant: textVariant,
    me: labelAlignment === 'start' ? '12' : undefined,
    ms: labelAlignment === 'end' ? '12' : undefined
  }, label)), labelInteractive && input, /*#__PURE__*/React.createElement(Box, _extends({}, isToggleButtonAccessibilityColorFix && _objectSpread({
    tabIndex: 0,
    'aria-checked': checked,
    'aria-disabled': disabled,
    'aria-label': ariaLabel !== null && ariaLabel !== void 0 ? ariaLabel : label,
    'aria-labelledby': label ? id : undefined,
    'aria-describedby': label ? "".concat(id, "-description") : undefined,
    'aria-hidden': labelInteractive ? 'true' : undefined,
    role: 'switch',
    onKeyDown: handleKeyDown
  }, boxProps), {
    as: labelInteractive ? undefined : 'label',
    boxSizing: hasTouch ? 'content-box' : undefined,
    display: "inline-flex",
    position: "relative",
    borderRadius: "max",
    outlineColor: "transparent",
    transition: "outline-color 0.2s ease-in-out",
    flexShrink: 0,
    tapHighlight: false,
    width: trackWidth,
    height: trackHeight,
    cursor: disabled ? undefined : 'pointer',
    p: !labelInteractive && hasTouch ? '8' : undefined
  }, !labelInteractive && rest), !labelInteractive && input, /*#__PURE__*/React.createElement(Box, {
    borderRadius: "max",
    outlineColor: "transparent",
    transition: "background-color 0.2s ease-in-out, outline-color 0.2s ease-in-out",
    width: "100%",
    height: "100%",
    minWidth: trackWidth,
    minHeight: trackHeight,
    backgroundColor: trackColor
  }), /*#__PURE__*/React.createElement(ToggleKnobContainer, {
    p: hasTouch && labelInteractive ? undefined : '8',
    m: hasTouch ? undefined : '-8',
    top: knobPadding,
    start: knobPadding,
    pointerEvents: disabled ? 'none' : undefined,
    transform: direction === Direction.LTR ? "translateX(".concat(knobTranslation, ")") : "translateX(-".concat(knobTranslation, ")"),
    $rippleSize: rippleSize,
    $rippleColor: rippleColor
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    backgroundColor: "base.surface",
    borderRadius: "max",
    alignItems: "center",
    justifyContent: "center",
    transition: "width 0.2s ease-in-out, height 0.2s ease-in-out",
    width: knobSize,
    height: knobSize
  }, checked && /*#__PURE__*/React.createElement(Icon, {
    id: "fill.symbol.check",
    color: disabled ? 'deprecated.neutral.disabled-2' : 'product.primary',
    size: hasTouch ? '16' : '12'
  })))));
});
Toggle.defaultProps = {
  labelAlignment: 'start',
  labelInteractive: true,
  textVariant: 'body-s-strong',
  state: 'enabled',
  width: 'parent-fit'
};
var ToggleMemoized = /*#__PURE__*/React.memo(Toggle);
ToggleMemoized.displayName = 'Toggle';

export { ToggleMemoized as Toggle, Toggle as ToggleWithoutMemoized };
//# sourceMappingURL=Toggle.js.map
